import { Button, Tooltip, Typography } from 'antd';
import { MinusOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export type PageMeta = {
  pageNumber: number;
  width: number;
  height: number;
  unit: string;
};
export type FieldHit = {
  value: string;
  pageNumber: number;
  polygons: number[][];
  score: number;
};

type Props = {
  base64: string;
  pages: PageMeta[];
  hits: Record<string, FieldHit | null>;
  scale?: number; // initial scale
  fileName?: string;
};

export default function PdfWithBoxes({
  base64,
  pages,
  hits,
  scale: initialScale = 1.5,
}: Props) {
  const [pageDimensions, setPageDimensions] = useState<
    Map<number, { width: number; height: number }>
  >(new Map());

  // --- Zoom state ---
  const [scale, setScale] = useState(initialScale);
  const zoomIn = () => setScale((s) => Math.min(s + 0.2, 3));
  const zoomOut = () => setScale((s) => Math.max(s - 0.2, 0.5));
  const resetZoom = () => setScale(initialScale);

  const dataUrl = useMemo(
    () =>
      base64.startsWith('data:')
        ? base64
        : `data:application/pdf;base64,${base64}`,
    [base64],
  );

  const overlays = useMemo(() => {
    const map = new Map<number, { label: string; polygons: number[][] }[]>();
    Object.entries(hits).forEach(([k, v]) => {
      if (v) {
        if (!map.has(v.pageNumber)) map.set(v.pageNumber, []);
        map.get(v.pageNumber)!.push({ label: k, polygons: v.polygons });
      }
    });
    return map;
  }, [hits]);

  const toRects = (polygon: number[], padding = 0.05) => {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < polygon.length; i += 2) {
      pts.push({ x: polygon[i], y: polygon[i + 1] });
    }
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs),
      maxX = Math.max(...xs);
    const minY = Math.min(...ys),
      maxY = Math.max(...ys);
    return {
      minX: minX - padding,
      minY: minY - padding,
      maxX: maxX + padding,
      maxY: maxY + padding,
    };
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#f5f5f5',
        padding: '1rem',
        marginTop: 5,
      }}
    >
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="Zoom Out">
          <Button icon={<MinusOutlined />} onClick={zoomOut} />
        </Tooltip>
        <Tooltip title="Reset Zoom">
          <Button icon={<ReloadOutlined />} onClick={resetZoom} />
        </Tooltip>
        <Tooltip title="Zoom In">
          <Button icon={<PlusOutlined />} onClick={zoomIn} />
        </Tooltip>
        <span style={{ marginLeft: 8 }}>Zoom: {(scale * 100).toFixed(0)}%</span>
      </div>

      <Document file={dataUrl} loading={<div>Loading PDF…</div>}>
        {pages.map((pm) => (
          <Page
            key={pm.pageNumber}
            pageNumber={pm.pageNumber}
            scale={scale}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onLoadSuccess={(page) => {
              setPageDimensions((prev) =>
                new Map(prev).set(pm.pageNumber, {
                  width: page.width,
                  height: page.height,
                }),
              );
            }}
          >
            {() => {
              const pageDim = pageDimensions.get(pm.pageNumber);
              if (!pageDim) return null;

              const sx = pageDim.width / pm.width;
              const sy = pageDim.height / pm.height;
              const items = overlays.get(pm.pageNumber) ?? [];

              return (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: pageDim.width,
                    height: pageDim.height,
                    pointerEvents: 'none',
                  }}
                >
                  {items.flatMap((it, idx) =>
                    it.polygons.map((poly, j) => {
                      const r = toRects(poly);
                      const left = r.minX * sx;
                      const top = r.minY * sy;
                      const w = (r.maxX - r.minX) * sx;
                      const h = (r.maxY - r.minY) * sy;
                      return (
                        <Tooltip
                          key={`${idx}-${j}`}
                          style={{
                            zIndex: 1000,
                            color: 'white',
                            background: 'black',
                          }}
                          title={
                            <Typography.Text
                              style={{ fontSize: 12, color: 'white' }}
                            >
                              {it.label
                                ?.split('_')
                                .map(
                                  (s) => s.charAt(0).toUpperCase() + s.slice(1),
                                )
                                .join(' ')}
                              : {hits[it.label]?.value}
                            </Typography.Text>
                          }
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left,
                              top,
                              width: w,
                              height: h,
                              border: `2px solid ${getRandomColor()}`,
                              borderRadius: 6,
                              pointerEvents: 'auto',
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      );
                    }),
                  )}
                </div>
              );
            }}
          </Page>
        ))}
      </Document>
    </div>
  );
}
